import { useEffect, useMemo, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Play } from "lucide-react";
import { useContentStore } from "@/stores/contentStore";
import { useWorldStore } from "@/stores/worldStore";
import { ContentKanban } from "./ContentKanban";
import { ContentCalendar } from "./ContentCalendar";
import { KeywordExplorer } from "./KeywordExplorer";
import { ContentStats } from "./ContentStats";
import { ContentItemDetail } from "./ContentItemDetail";
import { NewArticleModal } from "./NewArticleModal";

export function ContentDashboard() {
  const [, params] = useRoute("/projects/:id/content");
  const [, navigate] = useLocation();
  const projectId = params?.id ? parseInt(params.id, 10) : null;

  const activeTab = useContentStore((s) => s.activeTab);
  const fetchItems = useContentStore((s) => s.fetchItems);
  const selectedItemId = useContentStore((s) => s.selectedItemId);
  const isLoading = useContentStore((s) => s.loading[`items-${projectId}`]);
  const projects = useWorldStore((s) => s.projects);
  const project = useMemo(
    () => projects.find((p) => p.id === projectId),
    [projects, projectId]
  );

  const [showNewArticle, setShowNewArticle] = useState(false);

  useEffect(() => {
    if (projectId) {
      void fetchItems(projectId);
    }
  }, [projectId, fetchItems]);

  if (!projectId) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-border/40">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">
            {project?.name || `Project ${projectId}`}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewArticle(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            New Article
          </Button>
          <Button variant="outline" size="sm" disabled>
            <Play className="w-4 h-4 mr-1" />
            Run Batch
          </Button>
        </div>
      </div>

      {/* Content area — driven by sidebar tab selection */}
      <div className="flex-1 min-h-0 overflow-auto">
        {activeTab === "board" && (
          isLoading ? (
            <div className="p-6 space-y-4" role="status" aria-label="Loading content">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : (
            <ContentKanban projectId={projectId} />
          )
        )}

        {activeTab === "calendar" && (
          <div className="p-6">
            <ContentCalendar projectId={projectId} />
          </div>
        )}

        {activeTab === "keywords" && (
          <div className="p-6">
            <KeywordExplorer projectId={projectId} />
          </div>
        )}

        {activeTab === "stats" && (
          <div className="p-6">
            <ContentStats projectId={projectId} />
          </div>
        )}
      </div>

      {/* Side panel for item detail */}
      {selectedItemId && (
        <ContentItemDetail
          itemId={selectedItemId}
          projectId={projectId}
        />
      )}

      {/* New Article Modal */}
      <NewArticleModal
        isOpen={showNewArticle}
        onClose={() => setShowNewArticle(false)}
        projectId={projectId}
      />
    </div>
  );
}
